import cn from 'classnames';
import React, { memo, useMemo } from 'react';

import { Components } from '@sravni/vitrina/lib/enums/Components';
import { getComponentText } from '@sravni/vitrina/lib/helpers/stringHelper';
import type IWidgetsConfig from '@sravni/vitrina/lib/types/config/widget/IWidgetsConfig';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';
import { Label, Range, RowToolTip, StaticDate, StaticNumber, StaticString } from '@sravni/vitrina-components';

import Dictionary from '@components/RatingTable/components/ProductPopup/components/Dictionary';
import { useDictionaries, useProductConfig } from 'src/store/hooks/productPopupInfo';

import type { ICON_SIZES } from '../../../../constants/icon';
import Icon from '../Icon';

import style from './style.module.scss';

interface IWidgetProps {
  className?: string;
  iconClassName?: string;
  labelClassName: string;
  product: IProduct;
  config: IWidgetsConfig;
  iconSize?: typeof ICON_SIZES[keyof typeof ICON_SIZES];
}

const Widget: React.FC<IWidgetProps> = memo(
  ({ className, config, iconClassName, labelClassName, product, iconSize }) => {
    const vitrinsConfig = useProductConfig();
    const dictionaries = useDictionaries();
    const text = useMemo(
      () => (!config ? '' : getComponentText({ product, config: config.value, vitrinsConfig, dictionaries })),
      [config, product, vitrinsConfig, dictionaries],
    );

    const renderedElement = useMemo(() => {
      switch (config?.value?.component) {
        case Components.StaticNumber: {
          return (
            <StaticNumber
              product={product}
              componentConfig={config.value}
              className={style.value}
              formattedText={text}
            />
          );
        }
        case Components.StaticString: {
          return (
            <StaticString
              product={product}
              componentConfig={config.value}
              className={style.value}
              formattedText={text}
            />
          );
        }
        case Components.StaticDate: {
          return (
            <StaticDate product={product} componentConfig={config.value} className={style.value} formattedText={text} />
          );
        }
        case Components.Range: {
          return (
            <Range product={product} componentConfig={config.value} className={style.value} formattedText={text} />
          );
        }
        case Components.Dictionary: {
          return (
            <Dictionary className={style.value} product={product} componentConfig={config.value} formattedText={text} />
          );
        }
        default: {
          return null;
        }
      }
    }, [config?.value, product, text]);

    if (!text) {
      return null;
    }

    return (
      <div className={cn(style.wrapper, className)}>
        <div className={cn(style.icon, iconClassName)}>
          <Icon config={config.icon} size={iconSize} />
        </div>
        <div className={style.content}>
          <RowToolTip product={product} labelTooltip={config.value.labelTooltip}>
            {renderedElement}
          </RowToolTip>
          <Label className={cn(style.label, labelClassName)} componentConfig={config.title} product={product} />
        </div>
      </div>
    );
  },
);

export default Widget;
